// ./src/utils/eventTypeSearch.ts
//
// Finding an event type by what the host TYPES (ROADMAP A2b #62, owner 2026-09-29: "no oktoberfest, or bbq party…
// have synonyms for each present type so when user types something that can represent one of existing types then we
// just show our existing type").
//
// Searches every type — sub-types included (Christmas lives under Holiday Party and was unreachable before) — across
// its NAME, its CODE and its SYNONYMS, accent- and case-insensitively ("quinceanera" finds Quinceañera).
//
// Ranking, best first: the whole name or a synonym equals the query → a name or synonym starts with it → a word in
// either starts with it → it appears anywhere. ⚠ At the SAME level a type's NAME beats another type's SYNONYM (review,
// 2026-10-01: "conf" found Christening through "confirmation" before Conference, "mar" found Wedding through "marriage"
// before Market — 26 such) — a synonym widens what finds a type, it must never hide another one. Remaining ties keep
// catalog order. A type is listed once, with the synonym that
// matched when that is why it was found (so "xmas → Christmas Party" explains itself).
//
// ⚠ Pure: `npm run check-event-type-search` runs it on fixtures.

export interface SearchableEventType {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
  synonyms: string[];
}

export interface EventTypeMatch<T extends SearchableEventType> {
  type: T;
  /** The synonym that matched, when the NAME did not — shown as "matched “xmas”". */
  matchedSynonym: string | null;
}

/** Lowercase, accents removed, punctuation folded to spaces, whitespace collapsed. */
export const foldForSearch = (value: string | null | undefined): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** 0 = no match; higher = better. */
const scoreText = (text: string, query: string): number => {
  if (!text || !query) {
    return 0;
  }

  if (text === query) {
    return 4;
  }

  if (text.startsWith(query)) {
    return 3;
  }

  if (text.split(' ').some((word) => word.startsWith(query))) {
    return 2;
  }

  return text.includes(query) ? 1 : 0;
};

/** The minimum query length before searching — one letter matches half the catalog and helps nobody. */
export const MIN_TYPE_QUERY = 2;

export const searchEventTypes = <T extends SearchableEventType>(types: readonly T[], query: string): EventTypeMatch<T>[] => {
  const q = foldForSearch(query);

  if (q.length < MIN_TYPE_QUERY) {
    return [];
  }

  const scored: Array<{ match: EventTypeMatch<T>; score: number; byName: boolean; index: number }> = [];

  types.forEach((type, index) => {
    const nameScore = Math.max(scoreText(foldForSearch(type.name), q), scoreText(foldForSearch(type.code), q));
    let synonymScore = 0;
    let synonym: string | null = null;

    for (const s of type.synonyms ?? []) {
      const score = scoreText(foldForSearch(s), q);

      if (score > synonymScore) {
        synonymScore = score;
        synonym = s;
      }
    }

    const score = Math.max(nameScore, synonymScore);

    if (score > 0) {
      scored.push({ match: { type, matchedSynonym: synonymScore > nameScore ? synonym : null }, score, byName: nameScore >= synonymScore, index });
    }
  });

  return scored.sort((a, b) => b.score - a.score || Number(b.byName) - Number(a.byName) || a.index - b.index).map((s) => s.match);
};
